// 프로모션 코드 관련 유틸리티 함수들
import { supabase } from '../lib/supabase';

/**
 * 사용자의 활성 프로모션 코드 여부를 확인합니다.
 * @param {string} userId - 확인할 사용자 ID
 * @returns {Promise<{hasPromoCode: boolean, error: Error|null}>}
 */
export const checkUserPromoCode = async (userId) => {
  if (!userId) {
    return { hasPromoCode: false, error: null };
  }

  try {
    // 먼저 admin인지 확인 (순환 참조 방지를 위해 직접 조회)
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', userId)
      .maybeSingle();
    
    if (!profileError && profileData?.role === 'admin') {
      console.log('User is admin, has access to all features');
      return { hasPromoCode: true, error: null };
    }
    console.log('checkUserPromoCode: Checking for user', userId);
    
    // 1. promo_code_usage 테이블 확인
    const { data: promoUsageData, error: promoUsageError } = await supabase
      .from('promo_code_usage')
      .select('id, is_active, expires_at')
      .eq('user_id', userId)
      .eq('is_active', true);

    if (promoUsageError) {
      console.error('Error checking promo_code_usage:', promoUsageError);
    } else {
      //console.log('promo_code_usage data:', promoUsageData);
    }

    // 2. user_subscriptions 테이블도 확인
    const { data: subscriptionData, error: subscriptionError } = await supabase
      .from('user_subscriptions')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle(); // single() 대신 maybeSingle() 사용 - 레코드가 없어도 에러 안남

    if (subscriptionError) {
      console.error('Error checking user_subscriptions:', subscriptionError);
    } else {
      //console.log('user_subscriptions data:', subscriptionData);
    }

    // 활성 프로모 코드가 있는지 확인
    if (promoUsageData && promoUsageData.length > 0) {
      // 만료되지 않은 프로모 코드가 있는지 확인
      const activePromoCode = promoUsageData.find(promo => {
        if (!promo.expires_at) return true; // 만료일이 없으면 영구적
        return new Date(promo.expires_at) > new Date();
      });
      
      return { hasPromoCode: !!activePromoCode, error: null };
    }

    // user_subscriptions에서도 확인
    if (subscriptionData && subscriptionData.subscription_type === 'promo' && subscriptionData.subscription_status === 'active') {
      return { hasPromoCode: true, error: null };
    }

    //console.log('No active promo code or subscription found');
    return { hasPromoCode: false, error: null };
  } catch (error) {
    console.error('Error in checkUserPromoCode:', error);
    return { hasPromoCode: false, error };
  }
};

/**
 * 사용자의 관리자 권한 여부를 확인합니다.
 * @param {string} userId - 확인할 사용자 ID
 * @returns {Promise<{isAdmin: boolean, error: Error|null}>}
 */
export const checkUserIsAdmin = async (userId) => {
  if (!userId) {
    return { isAdmin: false, error: null };
  }

  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', userId)
      .maybeSingle(); // single() 대신 maybeSingle() 사용 - 프로필이 없어도 에러 안남

    if (error) {
      console.error('Error checking admin role:', error);
      return { isAdmin: false, error };
    }

    return { isAdmin: data?.role === 'admin', error: null };
  } catch (error) {
    console.error('Error in checkUserIsAdmin:', error);
    return { isAdmin: false, error };
  }
};
